import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { SummaryDataResponse } from 'api/models/SummaryDataResponse';
import { FirmsService } from 'api/services/FirmsService';
import {
  FIRM_SUMMARY_DEFINITION_DATA_DEFAULT_ERROR_MESSAGE,
  FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY,
  FIRM_SUMMARY_DEFINITION_DATA_STALE_TIME,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import { throwError } from 'utilities';
import {
  GetFirmSummaryDataReturn,
  UseGetFirmSummaryDataParams,
} from './types';

export type { SummaryDataResponse };
export type { SummaryRow } from 'api/models/SummaryRow';

const getFirmSummaryData = async (
  firmId: number,
  definitionId: number,
  measurementDate?: string
): Promise<SummaryDataResponse | null> => {
  try {
    const result = await FirmsService.apiFirmsSummaryDefinitionsDataRead(
      String(firmId),
      String(definitionId),
      measurementDate || undefined
    );

    if (!result) {
      return throwError(FIRM_SUMMARY_DEFINITION_DATA_DEFAULT_ERROR_MESSAGE);
    }

    return result;
  } catch (error) {
    return throwError(FIRM_SUMMARY_DEFINITION_DATA_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFirmSummaryData = (
  params: UseGetFirmSummaryDataParams
): GetFirmSummaryDataReturn => {
  const { firmId, definitionId, measurementDate, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetched, isFetching, isLoading, refetch } = useQuery<
    GetFirmSummaryDataReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && !isNil(definitionId) && shouldQueryAutomatically,
    queryFn: () => getFirmSummaryData(firmId!, definitionId!, measurementDate),
    queryKey: [FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY, firmId, definitionId, measurementDate],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
    staleTime: FIRM_SUMMARY_DEFINITION_DATA_STALE_TIME,
  });

  return {
    data,
    error,
    isError,
    isFetched,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFirmSummaryData;
